import React, { useState, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import SearchForm from './SearchForm';
import SearchResults from './SearchResults';
import SearchHeader from './SearchHeader';
import SearchModeToggle from './SearchModeToggle';
import ProductFilters, {
  TIER_OPTIONS,
  PRICE_OPTIONS,
  MOQ_OPTIONS,
} from './ProductFilters';
import ProductResults from './ProductResults';

const PAGE_SIZE = 40;
const PRODUCT_PAGE_SIZE = 24;

const PRODUCT_SORT_OPTIONS = [
  { value: '', label: 'Relevance' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
];

const EMPTY_PRODUCT_FILTERS = {
  categories: '',
  country: '',
  tier: '',
  price: '',
  moq_max: '',
  has_image: '',
};

function getInitialQuery() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('q') || '';
}

function getRfqId() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('rfq_id') || '';
}

function getInitialMode() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('mode') === 'products' ? 'products' : 'suppliers';
}

function getInitialFilters() {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    categories: searchParams.get('categories') || '',
    country: searchParams.get('country') || '',
    services: searchParams.get('services') || '',
    moq_range: searchParams.get('moq_range') || '',
    specialties: searchParams.get('specialties') || '',
    partner_types: searchParams.get('partner_types') || '',
  };
}

function getInitialProductFilters() {
  const searchParams = new URLSearchParams(window.location.search);

  return {
    categories: searchParams.get('categories') || '',
    country: searchParams.get('country') || '',
    tier: searchParams.get('tier') || '',
    price: searchParams.get('price') || '',
    moq_max: searchParams.get('moq_max') || '',
    has_image: searchParams.get('has_image') || '',
  };
}

function getInitialProductSort() {
  const searchParams = new URLSearchParams(window.location.search);
  return searchParams.get('sort') || '';
}

// URL sync builds the query string from scratch per mode so toggling never
// leaves stale params from the other mode behind.
function updateQueryParams(query, filters) {
  const searchParams = new URLSearchParams();
  searchParams.set('q', query);

  const rfqId = getRfqId();
  if (rfqId) searchParams.set('rfq_id', rfqId);
  if (filters.categories) searchParams.set('categories', filters.categories);
  if (filters.services) searchParams.set('services', filters.services);
  if (filters.country) searchParams.set('country', filters.country);
  if (filters.moq_range) searchParams.set('moq_range', filters.moq_range);
  if (filters.specialties) searchParams.set('specialties', filters.specialties);
  if (filters.partner_types) searchParams.set('partner_types', filters.partner_types);

  window.history.replaceState(null, null, '?' + searchParams.toString());
}

function updateProductQueryParams(query, filters, sort) {
  const searchParams = new URLSearchParams();
  searchParams.set('mode', 'products');
  searchParams.set('q', query);

  if (filters.categories) searchParams.set('categories', filters.categories);
  if (filters.country) searchParams.set('country', filters.country);
  if (filters.tier) searchParams.set('tier', filters.tier);
  if (filters.price) searchParams.set('price', filters.price);
  if (filters.moq_max) searchParams.set('moq_max', filters.moq_max);
  if (filters.has_image) searchParams.set('has_image', 'true');
  if (sort) searchParams.set('sort', sort);

  window.history.replaceState(null, null, '?' + searchParams.toString());
}

async function fetchSuppliersInfinite({ queryKey, pageParam }) {
  const [_key, { query, filters, rfqId }] = queryKey;
  const pageParams = `page_size=${PAGE_SIZE}&page_index=${pageParam}`;
  const filterParams = `country=${
    filters.country
  }&categories=${encodeURIComponent(
    filters.categories
  )}&in_house_services=${encodeURIComponent(filters.services)}&moq_range=${
    filters.moq_range
  }&specialties=${encodeURIComponent(filters.specialties)}&partner_types=${encodeURIComponent(filters.partner_types)}`;
  const rfqParam = rfqId ? `&rfq_id=${rfqId}` : '';
  const response = await fetch(
    `/search_suppliers?search_string=${query}&${filterParams}&${pageParams}${rfqParam}`,
    { method: 'POST' }
  );

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

async function fetchProductsInfinite({ queryKey, pageParam }) {
  const [_key, { query, filters, sort }] = queryKey;
  const searchParams = new URLSearchParams({
    search_string: query,
    page_index: pageParam,
    page_size: PRODUCT_PAGE_SIZE,
  });

  if (filters.categories) searchParams.set('categories', filters.categories);
  if (filters.country) searchParams.set('country', filters.country);
  if (filters.tier) searchParams.set('tier', filters.tier);
  if (filters.price) searchParams.set('price', filters.price);
  if (filters.moq_max) searchParams.set('moq_max', filters.moq_max);
  if (filters.has_image) searchParams.set('has_image', 'true');
  if (sort) searchParams.set('sort', sort);

  const response = await fetch(`/search_products?${searchParams.toString()}`, {
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error('Network response was not ok');
  }

  return response.json();
}

function Search() {
  const productSearchEnabled = !!window.searchData?.product_search_enabled;
  const rfqId = getRfqId();
  const rfqTitle = window.searchData?.rfq_title || null;

  const [query, setQuery] = useState(getInitialQuery());
  const [filters, setFilters] = useState(getInitialFilters());
  const [mode, setMode] = useState(
    productSearchEnabled && !rfqId ? getInitialMode() : 'suppliers'
  );
  const [productFilters, setProductFilters] = useState(getInitialProductFilters());
  const [productSort, setProductSort] = useState(getInitialProductSort());
  const [showFilters, setShowFilters] = useState(rfqId ? false : window.innerWidth > 768);
  const [viewMode, setViewMode] = useState('grid');
  const [animatedCount, setAnimatedCount] = useState(0);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['supplier-search', { query, filters, rfqId }],
      queryFn: fetchSuppliersInfinite,
      initialPageParam: 1,
      enabled: mode === 'suppliers',
      getNextPageParam: (lastPage) => {
        const { total_count, page_index, page_size } = lastPage;
        const hasMore = page_index * page_size < total_count;
        return hasMore ? page_index + 1 : undefined;
      },
    });

  const productSearch = useInfiniteQuery({
    queryKey: ['product-search', { query, filters: productFilters, sort: productSort }],
    queryFn: fetchProductsInfinite,
    initialPageParam: 1,
    enabled: productSearchEnabled && mode === 'products',
    getNextPageParam: (lastPage) => {
      const { total_count, page_index, page_size } = lastPage;
      const hasMore = page_index * page_size < total_count;
      return hasMore ? page_index + 1 : undefined;
    },
  });

  function handleSubmit(newQuery, newFilters) {
    // Don't allow the query to be cleared unless explicitly set to empty string
    const updatedQuery = typeof newQuery === 'string' ? newQuery : query;
    const updatedFilters = {
      categories: newFilters.categories || '',
      country: newFilters.country || '',
      services: newFilters.services || '',
      moq_range: newFilters.moq_range || '',
      specialties: newFilters.specialties || '',
      partner_types: newFilters.partner_types || '',
    };

    updateQueryParams(updatedQuery, updatedFilters);
    setQuery(updatedQuery);
    setFilters(updatedFilters);
  }

  // Product mode: the search bar submits here (the supplier-shaped filters the
  // form passes along are ignored).
  function handleProductQuerySubmit(newQuery) {
    const updatedQuery = typeof newQuery === 'string' ? newQuery : query;
    updateProductQueryParams(updatedQuery, productFilters, productSort);
    setQuery(updatedQuery);
  }

  function handleProductFiltersSubmit(newFilters) {
    const updatedFilters = {
      categories: newFilters.categories || '',
      country: newFilters.country || '',
      tier: newFilters.tier || '',
      price: newFilters.price || '',
      moq_max: newFilters.moq_max || '',
      has_image: newFilters.has_image || '',
    };

    updateProductQueryParams(query, updatedFilters, productSort);
    setProductFilters(updatedFilters);
  }

  function handleProductSortChange(newSort) {
    updateProductQueryParams(query, productFilters, newSort);
    setProductSort(newSort);
  }

  function handleModeChange(newMode) {
    if (newMode === mode) return;
    setMode(newMode);
    if (newMode === 'products') {
      updateProductQueryParams(query, productFilters, productSort);
    } else {
      updateQueryParams(query, filters);
    }
  }

  // Get filter options from window.searchData instead of first paged result
  const categoryOptions = window.searchData?.categories;
  const countryOptions = window.searchData?.countries;
  const serviceOptions = data?.pages?.[0]?.in_house_service_aggregates;
  const specialtyOptions = window.searchData?.specialties;
  const partnerTypeOptions = window.searchData?.partner_types;

  // Get total count from the first page of results
  const totalResults = data?.pages?.[0]?.total_count || 0;

  // Animate the counter when totalResults changes
  useEffect(() => {
    if (rfqId && totalResults > 0) {
      setAnimatedCount(0);
      const duration = 400; // 0.4 second animation
      const steps = 20;
      const increment = totalResults / steps;
      let current = 0;

      const timer = setInterval(() => {
        current += increment;
        if (current >= totalResults) {
          setAnimatedCount(totalResults);
          clearInterval(timer);
        } else {
          setAnimatedCount(Math.floor(current));
        }
      }, duration / steps);

      return () => clearInterval(timer);
    } else {
      setAnimatedCount(totalResults);
    }
  }, [totalResults, rfqId]);

  // Update the clearFilters function
  const clearFilters = () => {
    window.location.href = '/search';
  };

  const clearProductFilters = () => {
    setQuery('');
    setProductFilters(EMPTY_PRODUCT_FILTERS);
    setProductSort('');
    updateProductQueryParams('', EMPTY_PRODUCT_FILTERS, '');
  };

  // Function to remove a specific filter
  const removeFilter = (filterType, value) => {
    const newFilters = { ...filters };

    if (filterType === 'categories' || filterType === 'country' || filterType === 'services' || filterType === 'specialties' || filterType === 'partner_types') {
      // For comma-separated values, split and filter out the specific value
      const currentValues = newFilters[filterType] ? newFilters[filterType].split(',') : [];
      newFilters[filterType] = currentValues.filter(v => v !== value).join(',');
    } else {
      // For single values like moq_range
      newFilters[filterType] = '';
    }

    handleSubmit(query, newFilters);
  };

  const removeProductFilter = (filterType, value) => {
    const newFilters = { ...productFilters };

    if (filterType === 'categories' || filterType === 'country' || filterType === 'tier') {
      const currentValues = newFilters[filterType] ? newFilters[filterType].split(',') : [];
      newFilters[filterType] = currentValues.filter((v) => v !== value).join(',');
    } else {
      newFilters[filterType] = '';
    }

    handleProductFiltersSubmit(newFilters);
  };

  // Function to get display name for filter values
  const getFilterDisplayName = (filterType, value) => {
    if (filterType === 'country') {
      // countryOptions is an object with regions, need to search through all regions
      if (countryOptions) {
        for (const countries of Object.values(countryOptions)) {
          const country = countries.find(c => c.code === value);
          if (country) return country.name;
        }
      }
      return value;
    }
    if (filterType === 'tier') {
      return TIER_OPTIONS.find((tier) => tier.value === value)?.label || value;
    }
    if (filterType === 'price') {
      return PRICE_OPTIONS.find((price) => price.value === value)?.label || value;
    }
    if (filterType === 'moq_max') {
      return MOQ_OPTIONS.find((moq) => moq.value === value)?.label || value;
    }
    return value;
  };

  const hasActiveProductFilters = !!(
    query ||
    productFilters.categories ||
    productFilters.country ||
    productFilters.tier ||
    productFilters.price ||
    productFilters.moq_max ||
    productFilters.has_image
  );

  const productMode = mode === 'products';

  return (
    <>
      <SearchHeader rfqId={rfqId} />
      <div className="container is-fluid pt-5">
        <div className="columns">
          <div className="column">
            {rfqId && rfqTitle && (
              <div className="mb-1">
                <h2 className="title is-4 has-text-grey">
                  We found {animatedCount} matching factor{animatedCount === 1 ? 'y' : 'ies'} for your project "{rfqTitle}"
                </h2>
              </div>
            )}
            <div className="columns is-vcentered">
              {/* Sidebar with filters header */}
              <div className="column is-2">
                <div className="is-flex is-align-items-center">
                  {!rfqId && (
                    <button
                      className="button is-rounded mr-4 search-filters-button"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      <span className="icon">
                        <i
                          className={`ph-bold ${
                            showFilters ? 'ph-funnel' : 'ph-funnel-simple'
                          }`}
                        ></i>
                      </span>
                      <span>Filters</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Main content with search bar */}
              <div className="column">
                <div className="is-flex">
                  {!rfqId && productSearchEnabled && (
                    <SearchModeToggle mode={mode} onChange={handleModeChange} />
                  )}
                  {!rfqId && (
                    <div className="is-flex-grow-1 mr-3">
                      <SearchForm
                        initialQuery={query}
                        initialFilters={filters}
                        currentFilters={filters}
                        filterOptions={{
                          categories: categoryOptions,
                          countries: countryOptions,
                          services: serviceOptions,
                          specialties: specialtyOptions,
                          partner_types: partnerTypeOptions,
                        }}
                        onSubmit={productMode ? handleProductQuerySubmit : handleSubmit}
                        layout="search_only"
                        rfqTitle={rfqTitle}
                        placeholder={
                          productMode
                            ? 'Search products by name, material, supplier, and more'
                            : null
                        }
                      />
                    </div>
                  )}
                  {!rfqId && !productMode && (
                    <div className="buttons view-mode-toggle is-hidden-mobile">
                      <button
                        className={`button is-rounded ${
                          viewMode === 'grid' ? 'is-primary' : ''
                        }`}
                        onClick={() => setViewMode('grid')}
                      >
                        <span className="icon">
                          <i className="ph-bold ph-grid-four"></i>
                        </span>
                        <span>Grid</span>
                      </button>
                      <button
                        className={`button is-rounded ${
                          viewMode === 'list' ? 'is-primary' : ''
                        }`}
                        onClick={() => setViewMode('list')}
                      >
                        <span className="icon">
                          <i className="ph-bold ph-list"></i>
                        </span>
                        <span>List</span>
                      </button>
                    </div>
                  )}
                  {!rfqId && productMode && (
                    <div className="is-flex is-align-items-center is-hidden-mobile product-sort-select">
                      <div className="select is-rounded">
                        <select
                          value={productSort}
                          onChange={(e) => handleProductSortChange(e.target.value)}
                          aria-label="Sort products"
                        >
                          {PRODUCT_SORT_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filters Display — suppliers mode */}
            {!rfqId && !productMode && (query || filters.categories || filters.country || filters.services || filters.moq_range || filters.specialties || filters.partner_types) && (
              <div className="columns search-active-filters-row">
                <div className="column is-offset-2 search-active-filters-column">
                  <div className="tags are-small search-active-filters-tags">
                    {query && (
                      <span className="tag is-light">
                        Search: "{query}"
                        <button
                          className="delete is-small"
                          onClick={() => handleSubmit('', filters)}
                          aria-label="Remove search term"
                        ></button>
                      </span>
                    )}

                    {filters.categories && filters.categories.split(',').filter(v => v).map(category => (
                      <span key={`cat-${category}`} className="tag is-light">
                        Category: {category}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('categories', category)}
                          aria-label={`Remove ${category} filter`}
                        ></button>
                      </span>
                    ))}

                    {filters.country && filters.country.split(',').filter(v => v).map(country => (
                      <span key={`country-${country}`} className="tag is-light">
                        Location: {getFilterDisplayName('country', country)}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('country', country)}
                          aria-label={`Remove ${country} filter`}
                        ></button>
                      </span>
                    ))}

                    {filters.services && filters.services.split(',').filter(v => v).map(service => (
                      <span key={`service-${service}`} className="tag is-light">
                        Service: {service}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('services', service)}
                          aria-label={`Remove ${service} filter`}
                        ></button>
                      </span>
                    ))}

                    {filters.moq_range && (
                      <span className="tag is-light">
                        MOQ: {filters.moq_range}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('moq_range', filters.moq_range)}
                          aria-label="Remove MOQ filter"
                        ></button>
                      </span>
                    )}

                    {filters.specialties && filters.specialties.split(',').filter(v => v).map(specialty => (
                      <span key={`specialty-${specialty}`} className="tag is-light">
                        Attribute: {specialty}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('specialties', specialty)}
                          aria-label={`Remove ${specialty} filter`}
                        ></button>
                      </span>
                    ))}

                    {filters.partner_types && filters.partner_types.split(',').filter(v => v).map(partnerType => (
                      <span key={`partner-type-${partnerType}`} className="tag is-light">
                        Partner Type: {partnerType}
                        <button
                          className="delete is-small"
                          onClick={() => removeFilter('partner_types', partnerType)}
                          aria-label={`Remove ${partnerType} filter`}
                        ></button>
                      </span>
                    ))}

                    <a
                      href="#"
                      className="is-size-7 ml-2 search-clear-all-link"
                      onClick={(e) => {
                        e.preventDefault();
                        clearFilters();
                      }}
                    >
                      Clear all
                    </a>
                  </div>
                </div>
              </div>
            )}

            {/* Active Filters Display — products mode */}
            {!rfqId && productMode && hasActiveProductFilters && (
              <div className="columns search-active-filters-row">
                <div className="column is-offset-2 search-active-filters-column">
                  <div className="tags are-small search-active-filters-tags">
                    {query && (
                      <span className="tag is-light">
                        Search: "{query}"
                        <button
                          className="delete is-small"
                          onClick={() => handleProductQuerySubmit('')}
                          aria-label="Remove search term"
                        ></button>
                      </span>
                    )}

                    {productFilters.categories && productFilters.categories.split(',').filter(v => v).map(category => (
                      <span key={`pcat-${category}`} className="tag is-light">
                        Category: {category}
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('categories', category)}
                          aria-label={`Remove ${category} filter`}
                        ></button>
                      </span>
                    ))}

                    {productFilters.country && productFilters.country.split(',').filter(v => v).map(country => (
                      <span key={`pcountry-${country}`} className="tag is-light">
                        Location: {getFilterDisplayName('country', country)}
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('country', country)}
                          aria-label={`Remove ${country} filter`}
                        ></button>
                      </span>
                    ))}

                    {productFilters.tier && productFilters.tier.split(',').filter(v => v).map(tier => (
                      <span key={`ptier-${tier}`} className="tag is-light">
                        Tier: {getFilterDisplayName('tier', tier)}
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('tier', tier)}
                          aria-label={`Remove ${tier} filter`}
                        ></button>
                      </span>
                    ))}

                    {productFilters.price && (
                      <span className="tag is-light">
                        Price: {getFilterDisplayName('price', productFilters.price)}
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('price', productFilters.price)}
                          aria-label="Remove price filter"
                        ></button>
                      </span>
                    )}

                    {productFilters.moq_max && (
                      <span className="tag is-light">
                        MOQ: {getFilterDisplayName('moq_max', productFilters.moq_max)}
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('moq_max', productFilters.moq_max)}
                          aria-label="Remove MOQ filter"
                        ></button>
                      </span>
                    )}

                    {productFilters.has_image && (
                      <span className="tag is-light">
                        With photos only
                        <button
                          className="delete is-small"
                          onClick={() => removeProductFilter('has_image', productFilters.has_image)}
                          aria-label="Remove photos filter"
                        ></button>
                      </span>
                    )}

                    <a
                      href="#"
                      className="is-size-7 ml-2 search-clear-all-link"
                      onClick={(e) => {
                        e.preventDefault();
                        clearProductFilters();
                      }}
                    >
                      Clear all
                    </a>
                  </div>
                </div>
              </div>
            )}

            {!rfqId && <hr className="search-divider" />}
            <div className="columns">
              {/* Filters column - use CSS transition classes */}
              <div
                className={`column is-2 filters-column ${
                  !showFilters ? 'is-hidden' : ''
                }`}
              >
                {productMode ? (
                  <div key="product-filters" className="search-mode-pane">
                    <ProductFilters
                      filters={productFilters}
                      filterOptions={{
                        categories: categoryOptions,
                        countries: countryOptions,
                      }}
                      onSubmit={handleProductFiltersSubmit}
                    />
                  </div>
                ) : (
                  <div key="supplier-filters" className="search-mode-pane">
                    <SearchForm
                      initialQuery={query}
                      initialFilters={filters}
                      currentFilters={filters}
                      filterOptions={{
                        categories: categoryOptions,
                        countries: countryOptions,
                        services: serviceOptions,
                        specialties: specialtyOptions,
                        partner_types: partnerTypeOptions,
                      }}
                      onSubmit={handleSubmit}
                      layout="filters_only"
                    />
                  </div>
                )}
                <div className="mb-6">
                  <a href="/" className="button is-light is-fullwidth back-to-dashboard-button">
                    <span className="icon">
                      <i className="ph-bold ph-arrow-left"></i>
                    </span>
                    <span>Back to Dashboard</span>
                  </a>
                </div>
              </div>
              {/* Results column - adjust width based on showFilters */}
              <div className={`column ${showFilters ? 'is-10' : 'is-12'}`}>
                {productMode ? (
                  <div key="product-results" className="search-mode-pane">
                    <ProductResults
                      data={productSearch.data}
                      isLoading={productSearch.isLoading || !productSearch.data}
                      hasActiveFilters={hasActiveProductFilters}
                      onClearFilters={clearProductFilters}
                    />

                    <div className="my-6 has-text-centered">
                      {productSearch.hasNextPage && (
                        <button
                          className="button is-medium is-info px-6"
                          onClick={() => productSearch.fetchNextPage()}
                          disabled={
                            !productSearch.hasNextPage ||
                            productSearch.isFetchingNextPage
                          }
                        >
                          {productSearch.isFetchingNextPage
                            ? 'Loading more...'
                            : 'Load more products'}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div key="supplier-results" className="search-mode-pane">
                    <SearchResults
                      data={data}
                      isLoading={isLoading || !data}
                      viewMode={viewMode}
                      showFilters={showFilters}
                      rfqId={rfqId}
                    />

                    <div className="my-6 has-text-centered">
                      {hasNextPage && (
                        <button
                          className="button is-medium is-info px-6"
                          onClick={() => fetchNextPage()}
                          disabled={!hasNextPage || isFetchingNextPage}
                        >
                          {isFetchingNextPage
                            ? 'Loading more...'
                            : hasNextPage
                              ? 'Load more suppliers'
                              : 'No more to load'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Search;
